/**
 * The `GET /billing/status` vocabulary.
 *
 * Two ideas, kept apart on purpose. **Entitlement** — "does this person have
 * access?" — is computed across Stripe, RevenueCat and referrals. **Billing
 * relationship** — "who bills them, and where do they manage it?" — has exactly
 * one answer at a time. Today every client re-derives both from raw provider
 * fields, which is why two payment providers leak into every UI branch; here the
 * server answers "may I show a cancel button?" and the client never asks "is the
 * provider Stripe?".
 *
 * Lives in the contract package rather than `@listening/frontend/payments`
 * because `@listening/frontend` depends on this package, not the other way
 * round — `catalog.ts` re-exports the plan types so the payments package stays
 * the one place isomorphic payment code is imported from.
 *
 * @see docs/projects/active/refactor-payments/refactor-everything-payments.md — `GET /billing/status`
 */
import { Schema } from 'effect';

// ── Shared primitives ───────────────────────────────────────────────────────

/**
 * A billing date as a full UTC instant — `2027-03-22T01:00:00.000Z`, what
 * `Date#toISOString` produces.
 *
 * "ISO 8601" alone also admits `2027-03-22`, and a truncated date loses the time
 * of day: a period ending 01:00Z reads as a whole day later to a reader west of
 * UTC, and two surfaces parsing the same string — one at local midnight, one at
 * UTC midnight — disagree about the days remaining. Pinning the instant here is
 * what stops a date-only value reaching a client that cannot tell them apart.
 */
export const IsoInstant = Schema.String.pipe(
  Schema.check(
    Schema.isPattern(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/, {
      message: 'Must be a UTC instant, e.g. 2027-03-22T01:00:00.000Z',
      identifier: 'IsoInstant',
    }),
  ),
);

/**
 * Money the way Stripe stores it: whole cents, never negative.
 *
 * Integer, not Number: a NaN, an Infinity or a fractional cent reaching a price
 * display is a money bug, and the schema is the cheapest place to make it
 * impossible.
 */
export const CentAmount = Schema.Int.pipe(
  Schema.check(Schema.isGreaterThanOrEqualTo(0)),
);

// ── The plan catalog: closed for writes, open for reads ─────────────────────

/**
 * Everything a client is allowed to ask to be subscribed to.
 *
 * Closed, and adding a member is a product decision. The account carries **157
 * prices with live subscribers**, 143 of them with exactly one, so this is
 * emphatically not the set of prices that exist — it is the set that may be
 * *requested*. `{ plan_id: 'yearly_99' }` does not typecheck and does not
 * deserialize: unrepresentable, not rejected, the same standard the
 * server-evaluated trial length holds to.
 *
 * **Legacy prices are deliberately absent.** A `yearly_99` subscriber ($99/yr)
 * who moved to `yearly_default` ($39/yr) could never move back, because the
 * provisioning script deactivates `yearly_99` in Stripe — so the downgrade is a
 * one-way door, and the type is what stops us walking someone through it.
 *
 * **Membership is not an offer.** The set bounds what a request may *name*;
 * whether a plan may be bought is a server decision. `yearly_second_offer` is
 * refused with a 400 — it is not selectable and no promo code unlocks it — so
 * `GET /billing/checkout-config` is the list of what is purchasable, not this.
 */
export const PlanKey = Schema.Literals([
  'monthly_default',
  'yearly_default',
  'yearly_promo_59',
  'yearly_promo_69',
  'yearly_second_offer',
]).annotate({
  description:
    'The plan vocabulary a request may name. Closed set; legacy prices excluded. Membership is not an offer — `yearly_second_offer` is refused with a 400, and `GET /billing/checkout-config` lists what may actually be bought.',
});

export type PlanKey = typeof PlanKey.Type;

/**
 * The union's own members, never a second copy of them.
 *
 * A hand-written list would drift: `satisfies readonly PlanKey[]` rejects a
 * wrong entry but accepts a *short* one, so a plan added to `PlanKey` would go
 * unprovisioned in Stripe, unoffered on the paywall, and unrecognised by
 * `isPlanKey` — which strands that cohort with no in-app plan change, forever.
 */
export const PLAN_KEYS = PlanKey.literals;

/** Narrow an untrusted string without throwing. */
export const isPlanKey = (value: string | null | undefined): value is PlanKey =>
  (PLAN_KEYS as readonly string[]).includes(value ?? '');

export const BillingInterval = Schema.Literals([
  'day',
  'week',
  'month',
  'year',
]);
export type BillingInterval = typeof BillingInterval.Type;
export const isBillingInterval = Schema.is(BillingInterval);

/**
 * What a subscriber is on, built from the **Stripe price object** rather than a
 * catalog lookup — so every price the account has ever sold renders correctly,
 * including `0_upfront_47_2weeks` from 2016 and the 143 per-customer bespoke
 * prices. No read path may fail because a price is absent from the catalog.
 *
 * `plan_id` is `null` for anything outside the closed write set. That null is
 * load-bearing: it is what makes `can_change_plan_in_app` false, so we never
 * offer an in-app plan change that would strand someone on a price they cannot
 * return to.
 *
 * `currency` and `interval_count` are beyond the four fields the design doc
 * lists, and are kept: a descriptor that cannot say "£99 every 2 years" cannot
 * render one of the bespoke prices, which is the requirement this type exists
 * for.
 */
export const PlanDescriptor = Schema.Struct({
  /** `null` when the subscriber is on a price outside the closed write set. */
  plan_id: Schema.NullOr(PlanKey),
  interval: BillingInterval,
  amount_cents: CentAmount,
  display_name: Schema.String,
  currency: Schema.String,
  // At least one interval: Stripe's own floor, and "every 0 months" is not a
  // billing cycle any renderer can put a price against.
  interval_count: Schema.Int.pipe(Schema.check(Schema.isGreaterThan(0))),
});

export type PlanDescriptor = typeof PlanDescriptor.Type;

// ── The response ────────────────────────────────────────────────────────────

/**
 * Access, as one word.
 *
 * `none` covers both "never subscribed" and "free-tier eligible" — the free plan
 * is an access tier, not a billing object, so it surfaces in `free_tier` rather
 * than as a state here.
 */
export const EntitlementState = Schema.Literals([
  'none',
  'trialing',
  'active',
  'past_due',
  'canceled',
]);
export type EntitlementState = typeof EntitlementState.Type;

/**
 * Which provider is *currently granting* access, when one is.
 *
 * `comp` is a member even though nobody bills it: a comp grant entitles, and
 * reporting an active entitlement with `source: null` says "entitled by
 * nothing", which reads as a bug everywhere it lands — analytics first. `null`
 * means only what it should: nothing grants access.
 */
export const EntitlementSource = Schema.Literals([
  'stripe',
  'revenue_cat',
  'referral',
  'comp',
]);
export type EntitlementSource = typeof EntitlementSource.Type;

/**
 * Who bills this person, and therefore where they manage it.
 *
 * Exactly one answer at a time — unlike entitlement, which is a union across
 * providers.
 */
export const BillingStatusProvider = Schema.Literals([
  'stripe',
  'app_store',
  'play_store',
  'referral',
  'comp',
  'none',
]);
export type BillingStatusProvider = typeof BillingStatusProvider.Type;

/**
 * `GET /billing/status`.
 *
 * **The Stripe portal URL is deliberately absent.** Portal sessions expire in
 * five minutes, so the URL is minted on click by `POST /billing/portal-session`,
 * never on page load. `manage_url` is the App Store / Play management page and
 * is null for Stripe.
 */
export const BillingStatus = Schema.Struct({
  entitlement: Schema.Struct({
    state: EntitlementState,
    /** How long access runs, when known. A UTC instant, never a bare date. */
    access_until: Schema.NullOr(IsoInstant),
    source: Schema.NullOr(EntitlementSource),
  }),
  billing: Schema.Struct({
    provider: BillingStatusProvider,
    plan: Schema.NullOr(PlanDescriptor),
    /** The next *real* charge, never a $0 phase boundary. A UTC instant. */
    renews_at: Schema.NullOr(IsoInstant),
    cancel_at_period_end: Schema.Boolean,
    /**
     * False for an uncatalogued price, for schedule-bearing subscriptions, and
     * for a subscription that has already ended — the paywall is that user's
     * route, not a plan change.
     */
    can_change_plan_in_app: Schema.Boolean,
    can_cancel_in_app: Schema.Boolean,
    /**
     * A subscription still waiting for its card — an owed-time resubscribe or
     * a trial whose card form was abandoned: the plan its first real invoice
     * bills. Posting it to `/billing/subscribe` answers with the pending
     * SetupIntent, so a card form closed early can be finished later.
     */
    card_needed_for_plan: Schema.NullOr(PlanKey),
    /** App Store / Play management page. **Never** the Stripe portal. */
    manage_url: Schema.NullOr(Schema.String),
  }),
  free_tier: Schema.Struct({
    eligible: Schema.Boolean,
    reason: Schema.NullOr(Schema.String),
  }),
});

export type BillingStatus = typeof BillingStatus.Type;
